import React, { useState, useEffect } from 'react';
import {
  db,
  collection,
  doc,
  setDoc,
  addDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp
} from '../firebase';

function UnclaimedDevices({ portfolios, onBack, onPortfolioCreated }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimingDevice, setClaimingDevice] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [buildings, setBuildings] = useState([]);
  const [deviceName, setDeviceName] = useState('');
  const [showNewPortfolio, setShowNewPortfolio] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');

  // Load buildings when portfolio is selected
  useEffect(() => {
    if (!selectedPortfolio) {
      setBuildings([]);
      setSelectedBuilding('');
      return;
    }

    const unsubscribe = onSnapshot(
      collection(db, 'customer_portfolios', selectedPortfolio, 'buildings'),
      (snapshot) => {
        const buildingList = [];
        snapshot.forEach((doc) => {
          buildingList.push({ id: doc.id, ...doc.data() });
        });
        buildingList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setBuildings(buildingList);
      }
    );

    return () => unsubscribe();
  }, [selectedPortfolio]);

  // Listen for unclaimed devices
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'unclaimed_devices'),
      (snapshot) => {
        const deviceList = [];
        snapshot.forEach((doc) => {
          deviceList.push({ id: doc.id, ...doc.data() });
        });
        // Sort by registration time (newest first)
        deviceList.sort((a, b) => (b.registered_at?.toMillis() || 0) - (a.registered_at?.toMillis() || 0));
        setDevices(deviceList);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching unclaimed devices:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleClaimDevice = async (device) => {
    if (!selectedPortfolio || !deviceName.trim()) {
      alert('Please select a portfolio and enter a device name');
      return;
    }

    try {
      // Create device in the portfolio with optional building assignment
      // device.id is now the serial number (primary identifier)
      const deviceRef = doc(db, 'customer_portfolios', selectedPortfolio, 'devices', device.id);
      const deviceData = {
        name: deviceName.trim(),
        serial: device.id,
        sensors: device.sensors || [],
        status: 'active',
        assigned_at: serverTimestamp(),
        registered_at: device.registered_at || serverTimestamp()
      };

      // Only add building_id if a building was selected
      if (selectedBuilding) {
        deviceData.building_id = selectedBuilding;
      }

      await setDoc(deviceRef, deviceData);

      // Remove from unclaimed
      await deleteDoc(doc(db, 'unclaimed_devices', device.id));

      // Reset form
      setClaimingDevice(null);
      setSelectedPortfolio('');
      setSelectedBuilding('');
      setDeviceName('');

      alert('Device claimed successfully!');
    } catch (error) {
      console.error('Error claiming device:', error);
      alert('Failed to claim device: ' + error.message);
    }
  };

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) {
      alert('Please enter a portfolio name');
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'customer_portfolios'), {
        name: newPortfolioName.trim(),
        created_at: serverTimestamp()
      });

      setNewPortfolioName('');
      setShowNewPortfolio(false);

      // Notify parent to refresh portfolios
      if (onPortfolioCreated) {
        onPortfolioCreated();
      }

      alert('Portfolio created successfully!');
    } catch (error) {
      console.error('Error creating portfolio:', error);
      alert('Failed to create portfolio: ' + error.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) {
    return <div className="loading">Loading unclaimed devices...</div>;
  }

  return (
    <div className="unclaimed-devices">
      <div className="page-header">
        <button className="back-btn" onClick={onBack}>&larr; Back</button>
        <h2>Unclaimed Devices</h2>
        <button className="btn-new-portfolio" onClick={() => setShowNewPortfolio(true)}>
          + New Portfolio
        </button>
      </div>

      {showNewPortfolio && (
        <div className="new-portfolio-form">
          <h3>Create New Portfolio</h3>
          <div className="form-row">
            <input
              type="text"
              value={newPortfolioName}
              onChange={(e) => setNewPortfolioName(e.target.value)}
              placeholder="Portfolio name (e.g., Smith Restaurant Group)"
            />
            <button className="btn-primary" onClick={handleCreatePortfolio}>Create</button>
            <button className="btn-secondary" onClick={() => { setShowNewPortfolio(false); setNewPortfolioName(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {devices.length === 0 ? (
        <div className="empty-state">
          <p>No unclaimed devices</p>
          <p className="hint">New devices will appear here when they connect to the VPN and register.</p>
        </div>
      ) : (
        <div className="device-list">
          {devices.map((device) => (
            <div key={device.id} className="device-card">
              <div className="device-info">
                <h3>Serial: {device.id}</h3>
                <p>Sensors: {device.sensors?.length || 0} connected</p>
                <p className="timestamp">Registered: {formatDate(device.registered_at)}</p>
                {device.sensors?.length > 0 && (
                  <div className="sensor-ids">
                    {device.sensors.map((s, i) => (
                      <span key={i} className="sensor-tag">{s}</span>
                    ))}
                  </div>
                )}
              </div>

              {claimingDevice === device.id ? (
                <div className="claim-form">
                  <div className="form-group">
                    <label>Device Name</label>
                    <input
                      type="text"
                      value={deviceName}
                      onChange={(e) => setDeviceName(e.target.value)}
                      placeholder="e.g., Kitchen Fridge #1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Assign to Portfolio</label>
                    <select
                      value={selectedPortfolio}
                      onChange={(e) => setSelectedPortfolio(e.target.value)}
                    >
                      <option value="">Select portfolio...</option>
                      {portfolios.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Assign to Building <span className="optional">(optional)</span></label>
                    <select
                      value={selectedBuilding}
                      onChange={(e) => setSelectedBuilding(e.target.value)}
                      disabled={!selectedPortfolio}
                    >
                      <option value="">Leave for portfolio manager...</option>
                      {buildings.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-actions">
                    <button
                      className="btn-primary"
                      onClick={() => handleClaimDevice(device)}
                    >
                      Confirm
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setClaimingDevice(null);
                        setSelectedPortfolio('');
                        setSelectedBuilding('');
                        setDeviceName('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="btn-claim"
                  onClick={() => setClaimingDevice(device.id)}
                >
                  Claim Device
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <style>{`
        .unclaimed-devices {
          padding: 20px;
          background: #16213e;
          min-height: calc(100vh - 60px);
        }
        .page-header {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 30px;
        }
        .page-header h2 {
          color: #fff;
          margin: 0;
        }
        .btn-new-portfolio {
          margin-left: auto;
          background: #4ecdc4;
          color: #1a1a2e;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
        }
        .btn-new-portfolio:hover {
          background: #3dbdb5;
        }
        .new-portfolio-form {
          background: #2a2a4a;
          border: 1px solid #3a3a5a;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }
        .new-portfolio-form h3 {
          margin: 0 0 15px 0;
          color: #4ecdc4;
        }
        .form-row {
          display: flex;
          gap: 10px;
          align-items: center;
        }
        .form-row input {
          flex: 1;
          padding: 10px;
          border: 1px solid #3a3a5a;
          border-radius: 4px;
          font-size: 14px;
          background: #1a1a2e;
          color: #fff;
        }
        .back-btn {
          background: #2a2a4a;
          border: 1px solid #3a3a5a;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          color: #fff;
        }
        .back-btn:hover {
          background: #3a3a5a;
        }
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #888;
        }
        .empty-state .hint {
          font-size: 14px;
          color: #666;
        }
        .device-list {
          display: grid;
          gap: 20px;
        }
        .device-card {
          background: #2a2a4a;
          border: 1px solid #3a3a5a;
          border-radius: 8px;
          padding: 20px;
        }
        .device-info h3 {
          margin: 0 0 10px 0;
          font-family: monospace;
          font-size: 14px;
          color: #4ecdc4;
        }
        .device-info p {
          margin: 5px 0;
          color: #ccc;
        }
        .timestamp {
          font-size: 12px;
          color: #888;
        }
        .sensor-ids {
          margin-top: 10px;
        }
        .sensor-tag {
          display: inline-block;
          background: #3a3a5a;
          padding: 4px 8px;
          border-radius: 4px;
          font-family: monospace;
          font-size: 11px;
          margin: 2px;
          color: #4ecdc4;
        }
        .claim-form {
          margin-top: 15px;
          padding-top: 15px;
          border-top: 1px solid #3a3a5a;
        }
        .form-group {
          margin-bottom: 15px;
        }
        .form-group label {
          display: block;
          margin-bottom: 5px;
          font-weight: 600;
          font-size: 14px;
          color: #fff;
        }
        .form-group label .optional {
          font-weight: 400;
          color: #888;
          font-size: 12px;
        }
        .form-group input,
        .form-group select {
          width: 100%;
          padding: 10px;
          border: 1px solid #3a3a5a;
          border-radius: 4px;
          font-size: 14px;
          background: #1a1a2e;
          color: #fff;
        }
        .form-actions {
          display: flex;
          gap: 10px;
        }
        .btn-primary {
          background: #4ecdc4;
          color: #1a1a2e;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
        }
        .btn-primary:hover {
          background: #3dbdb5;
        }
        .btn-secondary {
          background: #3a3a5a;
          color: #fff;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
        }
        .btn-secondary:hover {
          background: #4a4a6a;
        }
        .btn-claim {
          margin-top: 15px;
          background: #4ecdc4;
          color: #1a1a2e;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          width: 100%;
          font-weight: 600;
        }
        .btn-claim:hover {
          background: #3dbdb5;
        }
      `}</style>
    </div>
  );
}

export default UnclaimedDevices;
